import { SignalIcon } from '@heroicons/react/24/outline';
import type { DmPatient } from '../../../../../services/dmPatient';
import { SavedText, StatusBadge } from './DmControlPrimitives';
import type { DmBaseline, DmDraft, DraftErrors, DraftPatch } from './dmControlDraft';
import { formatDmDate, formatYmd, fullName } from './dmControlUtils';
import type { ActionSteps } from './actionSteps';
import { MonitoringActions } from './MonitoringActions';
import { MonitoringSettings } from './MonitoringSettings';
import { ScanTimeline, type ScanTimelineProps } from './ScanTimeline';
import { useFlash } from './useFlash';

function subtitle(base: DmBaseline, patient: DmPatient): string {
    if (base.status === 'not_started') return 'Pick a quickstart to begin.';
    if (base.status === 'stopped') return `Stopped ${formatDmDate(patient.stopDate) ?? ''}`;
    if (base.status === 'paused') return `Resumes ${formatYmd(base.resumeOn)}`;
    const started = formatDmDate(patient.startDate);
    return started ? `Since ${started}` : '';
}

function timeline(base: DmBaseline, draft: DmDraft): ScanTimelineProps {
    if (base.status === 'stopped') return { empty: 'No scans scheduled. Monitoring has ended.' };
    if (base.status === 'not_started') {
        return { empty: 'The quickstart schedules the scans once monitoring starts.' };
    }
    const intervalDays = Number(draft.interval) || 1;
    if (base.status === 'paused' && base.resumeOn) {
        return { start: base.resumeOn, intervalDays, pausedUntil: base.resumeOn };
    }
    const start = draft.nextScan || base.nextScan;
    return start ? { start, intervalDays } : { empty: 'No scan scheduled yet.' };
}

interface MonitoringPanelProps {
    patientId: string;
    patient: DmPatient;
    base: DmBaseline;
    draft: DmDraft;
    errors: DraftErrors;
    set: (patch: DraftPatch) => void;
    /** A DM write is running: the fields and the status actions wait for it. */
    busy: boolean;
    steps: ActionSteps;
}

// Status actions on the left (applied at once), schedule and aligner on the right (saved by the
// footer), then what that schedule means.
export function MonitoringPanel({
    patientId,
    patient,
    base,
    draft,
    errors,
    set,
    busy,
    steps
}: MonitoringPanelProps) {
    const scans = timeline(base, draft);
    const [actionSaved, flashActionSaved] = useFlash<true>();
    return (
        <section className="mt-6">
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] divide-x divide-gray-200">
                {/* Status heading, then its actions straight under it. */}
                <div className="flex flex-col pr-8">
                    <div className="mb-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <SignalIcon className="h-5 w-5 shrink-0 text-[#007E87]" />
                            <h3 className="text-base font-semibold text-gray-800">Monitoring</h3>
                            <StatusBadge state={base.status} />
                            {actionSaved && <SavedText />}
                        </div>
                        <p className="mt-1 pl-7 text-xs text-gray-500">{subtitle(base, patient)}</p>
                    </div>
                    <fieldset disabled={busy} className="flex min-w-0 flex-1 flex-col">
                        <MonitoringActions
                            patientId={patientId}
                            patientName={fullName(patient)}
                            base={base}
                            alignerChangePending={patient.forceNextAlignerChange === true}
                            onDone={() => flashActionSaved(true)}
                            steps={steps}
                        />
                    </fieldset>
                </div>
                {/* Centred, so a taller status column splits the spare height above and below. */}
                <fieldset disabled={busy} className="flex min-w-0 flex-col justify-center pl-8">
                    <MonitoringSettings base={base} draft={draft} errors={errors} set={set} />
                </fieldset>
            </div>
            <div className="mt-6 border-t border-gray-100 pt-5">
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Upcoming scans</span>
                    {!('empty' in scans) && (
                        <span className="text-xs text-gray-400">
                            Every {scans.intervalDays} days
                            {scans.pausedUntil && ` · paused until ${formatYmd(scans.pausedUntil)}`}
                        </span>
                    )}
                </div>
                <ScanTimeline {...scans} />
            </div>
        </section>
    );
}
